import Link from 'next/link';
import { useRouter } from 'next/router';
import { type MouseEvent, memo, useCallback, useMemo, useRef, useState } from 'react';

import { useTrackClickedEllipsisMenuButton } from '@air/analytics';
import { InlineInput, type InlineInputProps } from '@air/component-inline-input';
import { CursorClickIcon, Ellipsis, Eye } from '@air/next-icons';
import { DropdownMenu, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { useToasts } from '@air/provider-toast';
import { itemIsSelectedSelector } from '@air/redux-selected-items';
import { reportErrorToBugsnag } from '@air/utils-error';
import { cn } from '@air/utils-tailwind';

import { useGetCanvasMenuOptionsCallback } from '~/components/Canvas/hooks/useGetCanvasMenuOptionsCallback';
import { DropHighlight } from '~/components/Shared/DropHighlight';
import { useCanEditCanvasListItem } from '~/components/v2/components/workspace/workspace-sidebar/useCanEditCanvasListItem';
import { useCanvasAssetDropTarget } from '~/components/v2/components/workspace/workspace-sidebar/useCanvasAssetDropTarget';
import { WorkspaceSidebarBoardsListItemButton } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsListItemButton';
import { useWorkspaceSidebarPushLock } from '~/components/v2/hooks/useWorkspaceSidebarPushLock';
import { useWorkspaceSidebarPushStore } from '~/components/v2/store/features/workspace/useWorkspaceSidebarPushStore';
import { CanvasQueryParams } from '~/constants/canvasQueryParams';
import { CANVAS_EXCLUDED_ROUTES, Routes } from '~/constants/routes';
import { usePrivateSelectionMenuOptions } from '~/hooks/usePrivateSelectionMenuOptions';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { type CanvasListItem } from '~/swr-hooks/canvases/types';
import { usePrivateCanvasesUpdateCanvasMutation } from '~/swr-hooks/canvases/useUpdateCanvas';
import { useAirSelector } from '~/utils/ReduxUtils';

export type WorkspaceSidebarCanvasesListItemProps = {
  canvas: CanvasListItem;
  isActive: boolean;
  onSelect: (canvasId: string) => void;
};

export const WorkspaceSidebarCanvasesListItem = memo(
  ({ canvas, isActive, onSelect }: WorkspaceSidebarCanvasesListItemProps) => {
    const router = useRouter();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isRenaming, setIsRenaming] = useState(false);
    const { canEdit: canEditCanvas, isLoading: isCanEditLoading } = useCanEditCanvasListItem(canvas);
    const { itemRef, isDragOver } = useCanvasAssetDropTarget({
      canEditCanvas,
      canvasId: canvas.id,
      onSelectCanvas: onSelect,
    });
    const { trackClickedEllipsisMenuButton } = useTrackClickedEllipsisMenuButton();
    const isSelected = useAirSelector((st) => itemIsSelectedSelector(st, canvas.id));
    const { getSelectionMenuOptions } = usePrivateSelectionMenuOptions();
    const { currentWorkspace } = useCurrentWorkspace();
    const { getCanvasMenuOptionsCallback } = useGetCanvasMenuOptionsCallback(canvas.boardId);
    const { showToast } = useToasts();
    const { mutateAsync: updateCanvas } = usePrivateCanvasesUpdateCanvasMutation();
    const isViewOnly = !canEditCanvas && !isCanEditLoading;
    const keepOverlayPinnedRef = useRef(false);
    const { handleOpenChange: handleOverlayLockChange } = useWorkspaceSidebarPushLock({
      token: `workspace-sidebar-canvas-${canvas.id}`,
    });

    const handleClick = useCallback(
      (e: MouseEvent<HTMLAnchorElement>) => {
        if (isRenaming) {
          return;
        }
        if (CANVAS_EXCLUDED_ROUTES.has(router.pathname)) {
          e.preventDefault();
        }
        onSelect(canvas.id);
      },
      [canvas.id, isRenaming, onSelect, router.pathname],
    );

    const handleMenuOpenChange = useCallback(
      (open: boolean) => {
        if (open) {
          handleOverlayLockChange(true);
          trackClickedEllipsisMenuButton({ location: 'canvas-item' });
        } else if (!keepOverlayPinnedRef.current) {
          handleOverlayLockChange(false);
        }
        setIsMenuOpen(open);
      },
      [handleOverlayLockChange, trackClickedEllipsisMenuButton],
    );

    const selectionMenuOptions = useMemo(
      () => getSelectionMenuOptions('gallery-multi-select-ellipsis-menu'),
      [getSelectionMenuOptions],
    );
    const ellipsisMenuOptions = useMemo(
      () =>
        getCanvasMenuOptionsCallback({
          item: canvas,
          trackLocation: 'gallery-item-ellipsis-menu',
        }),
      [canvas, getCanvasMenuOptionsCallback],
    );
    const openPush = useWorkspaceSidebarPushStore((s) => s.openPush);
    const handleRenameStart = useCallback(() => {
      keepOverlayPinnedRef.current = true;
      handleOverlayLockChange(true);
      openPush();
      setIsRenaming(true);
    }, [handleOverlayLockChange, openPush]);
    const handleRenameCancel = useCallback(() => {
      keepOverlayPinnedRef.current = false;
      handleOverlayLockChange(false);
      setIsRenaming(false);
    }, [handleOverlayLockChange]);
    const handleRenameSubmit = useCallback<Required<InlineInputProps>['onSubmit']>(
      async (value) => {
        if (value !== canvas.title && currentWorkspace?.id) {
          try {
            await updateCanvas({
              workspaceId: currentWorkspace.id,
              canvases: [{ canvasId: canvas.id, title: value }],
            });
          } catch (error) {
            showToast('There was an error updating the canvas.');
            reportErrorToBugsnag({
              context: 'Failed to update canvas',
              error,
              metadata: { canvasId: canvas.id },
            });
          }
        }
        keepOverlayPinnedRef.current = false;
        handleOverlayLockChange(false);
        setIsRenaming(false);
      },
      [canvas.id, canvas.title, currentWorkspace?.id, handleOverlayLockChange, showToast, updateCanvas],
    );
    const menuOptions = useMemo(() => {
      const baseOptions = isSelected && selectionMenuOptions.length ? selectionMenuOptions : ellipsisMenuOptions;
      const renameOptionIndex = baseOptions.findIndex((option) => option.type === 'item' && option.id === 'rename');

      if (renameOptionIndex === -1) {
        return baseOptions;
      }

      const nextOptions = [...baseOptions];
      const renameOption = nextOptions[renameOptionIndex];

      if (renameOption.type === 'item') {
        nextOptions[renameOptionIndex] = {
          ...renameOption,
          onSelect: handleRenameStart,
        };
      }

      return nextOptions;
    }, [ellipsisMenuOptions, handleRenameStart, isSelected, selectionMenuOptions]);
    const canvasHref = useMemo(() => {
      const isExcluded = CANVAS_EXCLUDED_ROUTES.has(router.pathname);

      if (isExcluded) {
        return { pathname: Routes.canvases, query: { [CanvasQueryParams.canvasId]: canvas.id } };
      }

      return { pathname: router.pathname, query: { ...router.query, [CanvasQueryParams.canvasId]: canvas.id } };
    }, [canvas.id, router.pathname, router.query]);
    const rowClassName = useMemo(
      () =>
        cn(
          'relative flex h-8 w-full cursor-pointer select-none items-center gap-2 rounded-8 px-3 pr-10 text-14 text-grey-11 no-underline hover:no-underline outline-none transition-colors group-hover:bg-grey-5 data-[menu-open=true]:bg-grey-5 focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset',
          (isActive || isDragOver) && 'bg-grey-5',
        ),
      [isActive, isDragOver],
    );

    return (
      <div className={cn('group relative', isMenuOpen && 'z-50')} ref={itemRef}>
        {canEditCanvas && <DropHighlight className={cn('rounded-8', isDragOver && 'border-blue-9 bg-grey-3')} />}
        {!isRenaming ? (
          <Link
            className={rowClassName}
            data-active={isActive}
            data-menu-open={isMenuOpen}
            data-testid="WORKSPACE_SIDEBAR_RECENT_CANVAS_ITEM"
            href={canvasHref}
            onClick={handleClick}
          >
            <CursorClickIcon className="relative z-10 size-4 shrink-0 text-grey-11" />
            <span className="relative z-10 truncate" title={canvas.title}>
              {canvas.title}
            </span>
            {isViewOnly && <Eye aria-hidden className="relative z-10 size-3 shrink-0 text-grey-9" />}
          </Link>
        ) : (
          <div
            className={rowClassName}
            data-active={isActive}
            data-menu-open={isMenuOpen}
            data-testid="WORKSPACE_SIDEBAR_RECENT_CANVAS_ITEM"
          >
            <CursorClickIcon className="relative z-10 size-4 shrink-0 text-grey-11" />
            <InlineInput
              allowNewLines={false}
              autoFocus
              className="z-40 w-full"
              data-testid="WORKSPACE_SIDEBAR_CANVAS_RENAME_INPUT"
              defaultValue={canvas.title}
              inputClassName="-ml-px"
              label="Canvas name"
              onCancel={handleRenameCancel}
              onSubmit={handleRenameSubmit}
              rows={1}
            />
          </div>
        )}

        {!isRenaming && menuOptions.length > 0 && (
          <div
            className={cn(
              'absolute top-1 right-2 transition-opacity',
              isMenuOpen
                ? 'pointer-events-auto opacity-100'
                : 'pointer-events-none opacity-0 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100',
            )}
          >
            <DropdownMenu
              onOpenChange={handleMenuOpenChange}
              open={isMenuOpen}
              trigger={
                <WorkspaceSidebarBoardsListItemButton data-testid="WORKSPACE_SIDEBAR_RECENT_CANVAS_ITEM_MENU_BUTTON">
                  <Ellipsis className="size-4" />
                </WorkspaceSidebarBoardsListItemButton>
              }
            >
              {renderDropdownItems({ options: menuOptions })}
            </DropdownMenu>
          </div>
        )}
      </div>
    );
  },
);

WorkspaceSidebarCanvasesListItem.displayName = 'WorkspaceSidebarCanvasesListItem';
